import { useEffect, useMemo, useRef } from 'react';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  applyNodeChanges, applyEdgeChanges,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Toast } from '../../helpers/alert';
import CalculationNode from './CalculationNode';
import NodePalette from './NodePalette';
import NodeInspector from './NodeInspector';
import ValidationPanel from './ValidationPanel';
import { NODE_PRESENTATION, initialConfigFor } from './nodePresentationRegistry';
import { addGraphNode, connectGraphEdge, removeGraphNodes } from './model';

const nodeTypes = Object.keys(NODE_PRESENTATION).reduce((result, type) => ({ ...result, [type]: CalculationNode }), {});
const ariaLabelConfig = {
  'node.a11yDescription.default': 'Presioná Enter o la barra espaciadora para seleccionar un nodo. Presioná Suprimir para eliminarlo y Escape para cancelar.',
  'node.a11yDescription.keyboardDisabled': 'Presioná Enter o la barra espaciadora para seleccionar un nodo. Usá las flechas para moverlo, Suprimir para eliminarlo y Escape para cancelar.',
  'node.a11yDescription.ariaLiveMessage': ({ direction, x, y }) => {
    const directions = { left: 'la izquierda', right: 'la derecha', up: 'arriba', down: 'abajo' };
    return `Nodo movido hacia ${directions[direction] || 'otra posición'}. Nueva posición: ${x}, ${y}.`;
  },
  'edge.a11yDescription.default': 'Presioná Enter o la barra espaciadora para seleccionar una conexión. Presioná Suprimir para eliminarla o Escape para cancelar.',
  'controls.ariaLabel': 'Controles del diagrama',
  'controls.zoomIn.ariaLabel': 'Acercar',
  'controls.zoomOut.ariaLabel': 'Alejar',
  'controls.fitView.ariaLabel': 'Ajustar el diagrama a la vista',
  'controls.interactive.ariaLabel': 'Cambiar el modo de interacción',
  'minimap.ariaLabel': 'Mapa general del diagrama',
  'handle.ariaLabel': 'Punto de conexión',
};

function EditorSurface({ nodes, edges, catalog, sourceCatalogs, sourceScope, selectedNodeId, onSelectionChange, onChange, validation, readOnly, focusRequest }) {
  const flowRef = useRef(null);
  const canvasRef = useRef(null);
  const addSequenceRef = useRef(0);
  const definitions = useMemo(() => new Map((catalog?.node_types || []).map(item => [item.type, item])), [catalog]);
  const renderedNodes = useMemo(() => nodes.map(node => ({
    ...node,
    data: { ...node.data, definition: definitions.get(node.data.nodeType || node.type) },
  })), [definitions, nodes]);
  const selectedNode = nodes.find(node => node.id === selectedNodeId) || null;

  const addNode = type => {
    const bounds = canvasRef.current?.getBoundingClientRect();
    const offset = (addSequenceRef.current++ % 6) * 22;
    const screenPoint = bounds
      ? { x: bounds.left + bounds.width / 2 + offset, y: bounds.top + bounds.height / 2 + offset }
      : { x: window.innerWidth / 2 + offset, y: window.innerHeight / 2 + offset };
    const flowPoint = flowRef.current?.screenToFlowPosition(screenPoint) || { x: 80 + offset, y: 80 + offset };
    const next = addGraphNode(nodes, type, { x: flowPoint.x - 90, y: flowPoint.y - 45 }, initialConfigFor(type));
    onChange(next, edges);
    onSelectionChange(next[next.length - 1].id);
  };
  const deleteNodes = ids => {
    const next = removeGraphNodes(nodes, edges, ids);
    onChange(next.nodes, next.edges);
    if (ids.includes(selectedNodeId)) onSelectionChange(null);
  };
  const selectAndFocus = id => {
    const node = nodes.find(item => item.id === id);
    if (!node) return;
    onSelectionChange(id);
    flowRef.current?.setCenter(node.position.x + 90, node.position.y + 45, { zoom: 1.2, duration: 350 });
  };

  useEffect(() => {
    if (focusRequest?.nodeId) selectAndFocus(focusRequest.nodeId);
    // La secuencia permite volver a enfocar el mismo nodo en revisiones sucesivas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest?.sequence]);

  return <div className="cm-editor-grid">
    <NodePalette catalog={catalog} onAdd={addNode} disabled={readOnly} />
    <main ref={canvasRef} className="cm-canvas" aria-label="Canvas del modelo de cálculo">
      <ReactFlow
        nodes={renderedNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onInit={instance => { flowRef.current = instance; }}
        onNodesChange={changes => {
          const removed = changes.filter(change => change.type === 'remove').map(change => change.id);
          if (removed.length) return deleteNodes(removed);
          onChange(applyNodeChanges(changes, nodes), edges);
        }}
        onEdgesChange={changes => onChange(nodes, applyEdgeChanges(changes, edges))}
        onConnect={connection => {
          const result = connectGraphEdge(nodes, edges, connection, catalog);
          if (result.error) return Toast.fire({ icon: 'warning', title: result.error });
          onChange(nodes, result.edges);
        }}
        onNodeClick={(_, node) => onSelectionChange(node.id)}
        onPaneClick={() => onSelectionChange(null)}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable
        deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
        fitView
        fitViewOptions={{ padding: 0.25 }}
        minZoom={0.2}
        maxZoom={2}
        ariaLabelConfig={ariaLabelConfig}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant="dots" gap={20} size={1} color="#dbe4ee" />
        <Controls showInteractive={!readOnly} />
        <MiniMap pannable zoomable nodeColor="#f2a900" maskColor="rgba(241,245,249,.68)" />
      </ReactFlow>
    </main>
    <NodeInspector node={selectedNode} nodes={nodes} edges={edges} catalog={catalog} sourceCatalogs={sourceCatalogs} sourceScope={sourceScope} disabled={readOnly} onDelete={id => deleteNodes([id])} onChange={(id, patch) => {
      onChange(nodes.map(node => node.id === id ? { ...node, data: { ...node.data, ...patch } } : node), edges);
    }} />
    <div className="cm-validation-row"><ValidationPanel validation={validation} onSelectNode={selectAndFocus} /></div>
  </div>;
}

export default function CalculationEditor(props) {
  return <ReactFlowProvider><EditorSurface {...props} /></ReactFlowProvider>;
}
